import { toRaw } from 'vue';

/**
 * Deep deserialize Vue Proxy objects recursively so payloads survive
 * structured cloning (BroadcastChannel.postMessage, Worker messages…).
 *
 * A shallow toRaw() is NOT enough: assigning `{ ...reactiveObj }` into a ref
 * stores nested reactive arrays/objects inside the raw target, and toRaw on
 * the top level never unwraps them — postMessage then throws DataCloneError
 * ("[object Array] could not be cloned").
 *
 * Only plain objects and arrays are traversed — recursing into DOM nodes,
 * Errors or class instances via property walk would hit prototype accessors
 * (e.g. an Event's `view` is window) and blow the stack. `seen` guards cycles.
 */
export function deepToRaw(obj: unknown, seen = new WeakSet<object>()): unknown {
	if (obj === null || typeof obj !== 'object') return obj;

	const raw = toRaw(obj) as object;
	if (seen.has(raw)) return undefined;

	if (Array.isArray(raw)) {
		seen.add(raw);
		return raw.map(item => deepToRaw(item, seen));
	}

	// Leave non-plain objects (Date, Error, DOM nodes, Map…) untouched.
	const proto = Object.getPrototypeOf(raw);
	if (proto !== Object.prototype && proto !== null) return raw;

	seen.add(raw);
	const result: Record<string, unknown> = {};
	for (const key of Object.keys(raw)) {
		result[key] = deepToRaw((raw as Record<string, unknown>)[key], seen);
	}
	return result;
}
